import {
    Box,
    Flex,
    HStack,
    Icon,
    Skeleton,
    Text,
    useColorModeValue,
} from '@chakra-ui/react';
import { NavLink } from 'react-router-dom';
import {
    MdArrowForward,
    MdAutoGraph,
    MdGroupAdd,
    MdRefresh,
    MdReceiptLong,
} from 'react-icons/md';
import { IconType } from 'react-icons';
import Card from '../../../components/card/Card';
import { translate } from '../../../i18n';
import { useSelectedLocation } from '../../location-management/hooks/useSelectedLocation';
import { useGetImpactSummary } from '../hooks/useGetImpactSummary';
import { UNKNOWN_STAT_VALUE } from '../../analytics/utils/statValue';
import { classifyRead } from '../../_shared/readState';

type Props = {
    startDate: Date;
    endDate: Date;
};

function WidgetMetric({
    icon,
    label,
    value,
    color,
    isPending,
    isError,
}: {
    icon: IconType;
    label: string;
    value: string | number;
    color: string;
    /**
     * Named for the state, not for the flag: the parent passes
     * `classifyRead(...) === 'loading'`, never the query's raw loading flag,
     * which React Query reports as `true` for a DISABLED query and which
     * would spin here forever.
     */
    isPending: boolean;
    isError: boolean;
}) {
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const textColorSecondary = 'secondaryGray.600';
    return (
        <HStack spacing='12px' flex='1' minW='0'>
            <Icon as={icon} w='28px' h='28px' color={color} flexShrink={0} />
            <Box minW='0'>
                {isPending ? (
                    <>
                        <Skeleton height='12px' width='80px' mb='4px' />
                        <Skeleton height='18px' width='60px' />
                    </>
                ) : (
                    <>
                        <Text color={textColorSecondary} fontSize='xs' noOfLines={1}>
                            {label}
                        </Text>
                        <Text
                            color={isError ? 'danger.500' : textColor}
                            fontSize='lg'
                            fontWeight='700'
                            letterSpacing='-0.02em'
                            role={isError ? 'status' : undefined}
                        >
                            {isError ? `${UNKNOWN_STAT_VALUE} ${translate('common.unknown')}` : value}
                        </Text>
                    </>
                )}
            </Box>
        </HStack>
    );
}

export default function HomepageImpactWidget({ startDate, endDate }: Props) {
    const { selectedLocation } = useSelectedLocation();
    const { data, isInitialLoading, isError } = useGetImpactSummary(
        selectedLocation?.id,
        startDate,
        endDate,
    );
    /**
     * Two states this widget could not express, on the operator's HOME screen.
     *
     * A refused `impact-summary` read (the route carries `AdminAuthGuard`) left
     * `data` undefined, and `data?.x ?? 0` reported a confident `0 spots
     * recovered / 0 self-service bookings / 0 new players` — the product
     * claiming it had done nothing, on the card that exists to say what it did.
     *
     * And the query is `enabled: !!locationId`: React Query v4 reports a
     * DISABLED query as `isLoading: true` with `fetchStatus: 'idle'`, so with no
     * location selected the three skeletons below ran forever.
     */
    /**
     * Both states from ONE classification of the query's own flags (R102-1,
     * closing D62): the predicate this replaced took `Boolean(locationId)` —
     * this hook's `enabled:` expression, restated in a second file, where it
     * can drift out of step with the hook without anything noticing.
     */
    const readState = classifyRead(isError, isInitialLoading, data);
    const unreadable = readState === 'unreadable';
    const pending = readState === 'loading';

    const brandColor = useColorModeValue('brand.500', 'white');
    const successColor = useColorModeValue('success.500', 'green.300');
    const warningColor = useColorModeValue('warning.500', 'orange.300');
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const headerBg = useColorModeValue('brand.50', 'whiteAlpha.100');

    return (
        <NavLink to='/admin/impact' style={{ width: '100%' }}>
            <Card
                p='0'
                cursor='pointer'
                transition='all 0.2s cubic-bezier(0.22, 0.61, 0.36, 1)'
                _hover={{
                    transform: 'translateY(-2px)',
                    boxShadow: 'md',
                }}
            >
                <Flex
                    align='center'
                    justify='space-between'
                    px='16px'
                    py='10px'
                    bg={headerBg}
                    borderTopRadius='inherit'
                >
                    <HStack spacing='8px'>
                        <Icon as={MdAutoGraph} w='18px' h='18px' color={brandColor} />
                        <Text color={textColor} fontSize='sm' fontWeight='700'>
                            {translate('impact.homepage_widget_title')}
                        </Text>
                    </HStack>
                    <Icon as={MdArrowForward} w='18px' h='18px' color={brandColor} />
                </Flex>
                <HStack
                    spacing={{ base: '16px', md: '24px' }}
                    p='16px'
                    align='center'
                    flexWrap={{ base: 'wrap', md: 'nowrap' }}
                >
                    <WidgetMetric
                        icon={MdRefresh}
                        label={translate('impact.spots_recovered')}
                        value={data?.replacementsFulfilled ?? 0}
                        color={successColor}
                        isPending={pending}
                        isError={unreadable}
                    />
                    <WidgetMetric
                        icon={MdReceiptLong}
                        label={translate('impact.self_service_bookings')}
                        value={data?.selfServiceBookings ?? 0}
                        color={brandColor}
                        isPending={pending}
                        isError={unreadable}
                    />
                    <WidgetMetric
                        icon={MdGroupAdd}
                        label={translate('impact.new_players')}
                        value={data?.newPlayers ?? 0}
                        color={warningColor}
                        isPending={pending}
                        isError={unreadable}
                    />
                </HStack>
            </Card>
        </NavLink>
    );
}
